import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import {
  DefaultValues,
  FieldPath,
  FieldValues,
  FormProvider,
  SubmitHandler,
  ValidationMode,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AnyObjectSchema } from 'yup';
import { FormValidationContext, Unvalidated } from 'domains/form-validation';
import { useFlashMessages } from 'domains/flash-messages';
import { DirtyFormGuard } from 'components/Forms';
import { ValidationError } from 'domains/api/errors';

type ServerErrors<T> = { [K in keyof T]?: string } & { nonFieldErrors?: string[] };

export type Props<T extends Record<string, unknown>> = {
  schema?: AnyObjectSchema;
  className?: string;
  disabled?: boolean;
  children?: React.ReactNode;
  defaultValues?: DefaultValues<Unvalidated<T>>;
  serverErrors?: ServerErrors<T>;
  onSubmit: SubmitHandler<T>;

  /**
   * Override the default flash error message that's displayed when there
   * are validation errors after form submit.
   */
  flashErrorMessage?: string;

  /**
   * Set to false to disable flash error messages for this form
   */
  flashErrors?: boolean;

  /**
   * If `true`, a dialog will be displayed if a user has partially filled out
   * the form and takes an action (other than submitting the form) that would
   * result in leaving the page and losing the form's state.
   */
  guardIfDirty?: boolean;

  validationMode?: keyof ValidationMode;

  /**
   * Order in which input errors should be displayed. If not provided, errors
   * will be displayed in the order they are encountered.
   */
  inputErrorsOrder?: string[];
};

export const Form = <T extends FieldValues>({
  schema,
  className,
  disabled = false,
  children,
  defaultValues,
  serverErrors = {},
  onSubmit,
  flashErrorMessage,
  flashErrors = true,
  guardIfDirty = true,
  validationMode = 'onSubmit',
  inputErrorsOrder,
}: Props<T>): JSX.Element => {
  const { t } = useTranslation();
  const { showFlash, clearFlash } = useFlashMessages();

  const schemaDefaults = schema ? schema.getDefault() : {};

  const form = useForm<Unvalidated<T>>({
    defaultValues: { ...schemaDefaults, ...defaultValues },
    resolver: schema ? yupResolver(schema) : undefined,
    // @NOTE(shawk): see comment below - this doesn't work well with disabling
    // inputs while submit is pending.
    shouldFocusError: false,
    mode: validationMode,
  });

  const { errors, isValid, isSubmitted, isSubmitting, isSubmitSuccessful, submitCount } = form.formState;

  useEffect(() => {
    const errorsCount = Object.keys(errors).length;

    /**
     * We have to check that `submitCount` > 1 here to ensure that we don't
     * call `showFlash` twice upon the initial form submission since the effect
     * will fire both with `submitCount` is 0 and when it changes to 1. We need
     * to include `submitCount` as a dependency of the effect so that re-submissions
     * with the same `errorsCount` do result in a flash message being displayed
     */
    if (isSubmitted && submitCount > 0 && flashErrors) {
      if (isValid && isSubmitSuccessful) {
        clearFlash();
      } else if (errorsCount) {
        showFlash([
          {
            variant: 'error',
            message: flashErrorMessage || t('form.feedback.flashFixErrors', { count: errorsCount }),
            key: 'form-feedback',
          },
        ]);
      }
    }
  }, [
    t,
    clearFlash,
    showFlash,
    flashErrorMessage,
    errors,
    isSubmitted,
    isSubmitSuccessful,
    isValid,
    submitCount,
    flashErrors,
  ]);

  /**
   * Focus the first invalid form field after submit if there are errors.
   *
   * @NOTE(shawk): Unfortunately, react-hook-form doesn't have good support
   * for `shouldFocusError: true` combined with disabling form inputs while
   * `formState.isSubmitting` is `true`. The library attempts to call `.focus()`
   * on the input with error before `disabled` flips back to `false` which fails
   * since disabled inputs aren't focusable. This is a workaround (managing the
   * focus ourselves after `isSubmitting` is no longer `true`).
   *
   * ref: https://github.com/react-hook-form/react-hook-form/discussions/3024
   * ref: https://github.com/react-hook-form/react-hook-form/issues/4120
   */
  useEffect(() => {
    if (!isSubmitting && !isValid && Object.keys(errors).length) {
      if (inputErrorsOrder) {
        const fieldPath = inputErrorsOrder.find(field => field in errors) as FieldPath<Unvalidated<T>>;
        form.setFocus(fieldPath);
      } else {
        const fieldPath = Object.keys(errors)[0] as FieldPath<Unvalidated<T>>;
        form.setFocus(fieldPath);
      }
    }
  }, [form, isSubmitting, isValid, errors]);

  return (
    <FormProvider {...form}>
      <FormValidationContext.Provider value={{ schema, disabled, serverErrors }}>
        {guardIfDirty && <DirtyFormGuard />}

        <form
          noValidate
          className={className}
          onSubmit={event => {
            clearFlash();

            return form
              .handleSubmit(onSubmit as SubmitHandler<Unvalidated<T>>)(event)
              .catch(error => {
                /**
                 * Bubble up non-validation errors encountered during submission.
                 * This assumes validation errors are handled in the UI.
                 */
                if (!(error instanceof ValidationError)) {
                  throw error;
                }
              });
          }}
        >
          {children}
        </form>
      </FormValidationContext.Provider>
    </FormProvider>
  );
};
